import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, User, Mail, Phone, FileText, Check, Loader2, ExternalLink, ClipboardList, CreditCard } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import ConsultationForm from './ConsultationForm';
import PaymentModal from './PaymentModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ZAPPY_URL = "https://pt.zappysoftware.com/m/iprbckamit";

// ALL categories require consultation form for complete client data collection
const REQUIRES_CONSULTATION = ['massage', 'body_care', 'spa_formulas', 'facial_care', 'afro_braids', 'afro_treatments', 'lashes_brows', 'manicure', 'pedicure', 'waxing', 'makeup'];

const translations = {
  en: {
    title: 'Book Your Appointment',
    subtitle: 'Choose your service and preferred time',
    selectCategory: 'Select Category',
    selectService: 'Select Service',
    selectDate: 'Select Date',
    selectTime: 'Select Time',
    yourInfo: 'Your Information',
    name: 'Full Name',
    email: 'Email',
    phone: 'Phone Number',
    notes: 'Additional Notes (optional)',
    submit: 'Confirm Booking',
    submitting: 'Submitting...',
    success: 'Booking Confirmed!',
    successMsg: 'We will contact you shortly to confirm your appointment.',
    consultationRequired: 'Please complete the consultation form for your spa/massage service.',
    fillConsultation: 'Fill Consultation Form',
    skipForNow: 'Skip for now',
    error: 'Error submitting booking. Please try again.',
    orUseZappy: 'Or book via Zappy',
    zappyLink: 'Book on Zappy',
    closedDay: 'Closed',
    noSlots: 'No available slots',
    payNow: 'Pay Now',
    payLater: 'Pay at salon',
    orPay: 'Or pay for your service',
    categories: {
      massage: 'Massages',
      body_care: 'Body Care',
      spa_formulas: 'Spa Formulas',
      facial_care: 'Facial Care',
      afro_braids: 'Afro Hair - Braids',
      afro_treatments: 'Afro Hair - Treatments',
      lashes_brows: 'Lashes & Brows',
      manicure: 'Manicure',
      pedicure: 'Pedicure',
      waxing: 'Waxing'
    }
  },
  fr: {
    title: 'Réserver Votre Rendez-vous',
    subtitle: 'Choisissez votre service et votre horaire préféré',
    selectCategory: 'Sélectionner la Catégorie',
    selectService: 'Sélectionner le Service',
    selectDate: 'Sélectionner la Date',
    selectTime: 'Sélectionner l\'Heure',
    yourInfo: 'Vos Informations',
    name: 'Nom Complet',
    email: 'Email',
    phone: 'Numéro de Téléphone',
    notes: 'Notes Supplémentaires (optionnel)',
    submit: 'Confirmer la Réservation',
    submitting: 'Envoi en cours...',
    success: 'Réservation Confirmée !',
    successMsg: 'Nous vous contacterons bientôt pour confirmer votre rendez-vous.',
    consultationRequired: 'Veuillez compléter le formulaire de consultation pour votre service spa/massage.',
    fillConsultation: 'Remplir le Formulaire de Consultation',
    skipForNow: 'Passer pour le moment',
    error: 'Erreur lors de la soumission. Veuillez réessayer.',
    orUseZappy: 'Ou réservez via Zappy',
    zappyLink: 'Réserver sur Zappy',
    closedDay: 'Fermé',
    noSlots: 'Aucun créneau disponible',
    payNow: 'Payer Maintenant',
    payLater: 'Payer au salon',
    orPay: 'Ou payez votre service',
    categories: {
      massage: 'Massages',
      body_care: 'Soins Corporels',
      spa_formulas: 'Formules Spa',
      facial_care: 'Soins du Visage',
      afro_braids: 'Coiffure Afro - Tresses',
      afro_treatments: 'Coiffure Afro - Traitements',
      lashes_brows: 'Cils & Sourcils',
      manicure: 'Manucure',
      pedicure: 'Pédicure',
      waxing: 'Épilation'
    }
  },
  pt: {
    title: 'Reservar a Sua Consulta',
    subtitle: 'Escolha o seu serviço e horário preferido',
    selectCategory: 'Selecionar Categoria',
    selectService: 'Selecionar Serviço',
    selectDate: 'Selecionar Data',
    selectTime: 'Selecionar Hora',
    yourInfo: 'As Suas Informações',
    name: 'Nome Completo',
    email: 'Email',
    phone: 'Número de Telefone',
    notes: 'Notas Adicionais (opcional)',
    submit: 'Confirmar Reserva',
    submitting: 'A enviar...',
    success: 'Reserva Confirmada!',
    successMsg: 'Entraremos em contacto em breve para confirmar a sua consulta.',
    consultationRequired: 'Por favor complete o formulário de consulta para o seu serviço spa/massagem.',
    fillConsultation: 'Preencher Formulário de Consulta',
    skipForNow: 'Saltar por agora',
    error: 'Erro ao submeter. Por favor tente novamente.',
    orUseZappy: 'Ou reserve via Zappy',
    zappyLink: 'Reservar no Zappy',
    closedDay: 'Fechado',
    noSlots: 'Sem horários disponíveis',
    payNow: 'Pagar Agora',
    payLater: 'Pagar no salão',
    orPay: 'Ou pague pelo seu serviço',
    categories: {
      massage: 'Massagens',
      body_care: 'Tratamentos Corporais',
      spa_formulas: 'Fórmulas Spa',
      facial_care: 'Cuidados Faciais',
      afro_braids: 'Cabelo Afro - Tranças',
      afro_treatments: 'Cabelo Afro - Tratamentos',
      lashes_brows: 'Pestanas & Sobrancelhas',
      manicure: 'Manicure',
      pedicure: 'Pedicure',
      waxing: 'Depilação'
    }
  },
  de: {
    title: 'Termin Buchen',
    subtitle: 'Wählen Sie Ihren Service und Ihre bevorzugte Zeit',
    selectCategory: 'Kategorie Auswählen',
    selectService: 'Service Auswählen',
    selectDate: 'Datum Auswählen',
    selectTime: 'Zeit Auswählen',
    yourInfo: 'Ihre Informationen',
    name: 'Vollständiger Name',
    email: 'E-Mail',
    phone: 'Telefonnummer',
    notes: 'Zusätzliche Notizen (optional)',
    submit: 'Buchung Bestätigen',
    submitting: 'Wird gesendet...',
    success: 'Buchung Bestätigt!',
    successMsg: 'Wir werden Sie in Kürze kontaktieren, um Ihren Termin zu bestätigen.',
    error: 'Fehler beim Senden. Bitte versuchen Sie es erneut.',
    orUseZappy: 'Oder über Zappy buchen',
    zappyLink: 'Auf Zappy Buchen',
    closedDay: 'Geschlossen',
    noSlots: 'Keine verfügbaren Slots',
    consultationRequired: 'Bitte füllen Sie das Beratungsformular für Ihren Service aus.',
    fillConsultation: 'Beratungsformular Ausfüllen',
    skipForNow: 'Jetzt überspringen',
    payNow: 'Jetzt Bezahlen',
    payLater: 'Im Salon bezahlen',
    orPay: 'Oder bezahlen Sie für Ihren Service',
    categories: {
      massage: 'Massagen',
      body_care: 'Körperpflege',
      spa_formulas: 'Spa-Formeln',
      facial_care: 'Gesichtspflege',
      afro_braids: 'Afro Haar - Zöpfe',
      afro_treatments: 'Afro Haar - Behandlungen',
      lashes_brows: 'Wimpern & Augenbrauen',
      manicure: 'Maniküre',
      pedicure: 'Pediküre',
      waxing: 'Wachsen'
    }
  },
  es: {
    title: 'Reservar Su Cita',
    subtitle: 'Elija su servicio y horario preferido',
    selectCategory: 'Seleccionar Categoría',
    selectService: 'Seleccionar Servicio',
    selectDate: 'Seleccionar Fecha',
    selectTime: 'Seleccionar Hora',
    yourInfo: 'Su Información',
    name: 'Nombre Completo',
    email: 'Correo Electrónico',
    phone: 'Número de Teléfono',
    notes: 'Notas Adicionales (opcional)',
    submit: 'Confirmar Reserva',
    submitting: 'Enviando...',
    success: '¡Reserva Confirmada!',
    successMsg: 'Nos pondremos en contacto pronto para confirmar su cita.',
    error: 'Error al enviar. Por favor intente de nuevo.',
    orUseZappy: 'O reserve a través de Zappy',
    zappyLink: 'Reservar en Zappy',
    closedDay: 'Cerrado',
    noSlots: 'Sin horarios disponibles',
    consultationRequired: 'Por favor complete el formulario de consulta para su servicio.',
    fillConsultation: 'Completar Formulario de Consulta',
    skipForNow: 'Saltar por ahora',
    payNow: 'Pagar Ahora',
    payLater: 'Pagar en el salón',
    orPay: 'O pague por su servicio',
    categories: {
      massage: 'Masajes',
      body_care: 'Cuidado Corporal',
      spa_formulas: 'Fórmulas Spa',
      facial_care: 'Cuidado Facial',
      afro_braids: 'Cabello Afro - Trenzas',
      afro_treatments: 'Cabello Afro - Tratamientos',
      lashes_brows: 'Pestañas y Cejas',
      manicure: 'Manicura',
      pedicure: 'Pedicura',
      waxing: 'Depilación'
    }
  }
};

const BookingModal = ({ isOpen, onClose, preselectedCategory = null, preselectedService = null }) => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  
  const [step, setStep] = useState(1);
  const [services, setServices] = useState({});
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [createdBooking, setCreatedBooking] = useState(null);
  const [showConsultation, setShowConsultation] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [consultationCompleted, setConsultationCompleted] = useState(false);
  
  const [formData, setFormData] = useState({
    category: preselectedCategory || '',
    service: preselectedService || '',
    servicePrice: '',
    date: '',
    time: '',
    name: '',
    email: '',
    phone: '',
    notes: ''
  });

  // Fetch services on mount
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services-for-booking`);
        setServices(response.data);
      } catch (err) {
        console.error('Error fetching services:', err);
      }
    };
    if (isOpen) {
      fetchServices();
    }
  }, [isOpen]);

  // Fetch available slots when date changes
  useEffect(() => {
    const fetchSlots = async () => {
      if (!formData.date) return;
      
      setLoading(true);
      try {
        const response = await axios.get(`${API}/bookings/available-slots?date=${formData.date}`);
        setAvailableSlots(response.data.available_slots);
      } catch (err) {
        console.error('Error fetching slots:', err);
        setAvailableSlots([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSlots();
  }, [formData.date]);

  // Check if selected day is closed (Thursday=4, Sunday=0)
  const isClosedDay = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const day = date.getDay();
    return day === 0 || day === 4; // Sunday or Thursday
  };

  const handleCategoryChange = (category) => {
    setFormData(prev => ({ ...prev, category, service: '', servicePrice: '' }));
  };

  const handleServiceChange = (serviceId) => {
    const serviceList = services[formData.category] || [];
    const selected = serviceList.find(s => s.id === serviceId);
    setFormData(prev => ({ 
      ...prev, 
      service: selected?.name || '', 
      servicePrice: selected?.price || '' 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/bookings`, {
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        service_category: formData.category,
        service_name: formData.service,
        service_price: formData.servicePrice,
        booking_date: formData.date,
        booking_time: formData.time,
        notes: formData.notes,
        language: language
      });
      
      setCreatedBooking(response.data);
      setSuccess(true);
    } catch (err) {
      console.error('Error creating booking:', err);
      setError(t.error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSuccess(false);
    setError(null);
    setCreatedBooking(null);
    setShowConsultation(false);
    setShowPayment(false);
    setConsultationCompleted(false);
    setFormData({
      category: '',
      service: '',
      servicePrice: '',
      date: '',
      time: '',
      name: '',
      email: '',
      phone: '',
      notes: ''
    });
    onClose();
  };

  const handleConsultationComplete = () => {
    setShowConsultation(false);
    setConsultationCompleted(true);
    // After consultation, show payment options
    setShowPayment(true);
  };

  const handlePaymentComplete = (method) => {
    setShowPayment(false);
    // Redirect to Zappy for final confirmation
    window.open(ZAPPY_URL, '_blank');
    handleClose();
  };

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Get maximum date (3 months from now)
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 3);
    return maxDate.toISOString().split('T')[0];
  };

  if (!isOpen) return null;

  // If showing payment modal
  if (showPayment && createdBooking) {
    return (
      <PaymentModal
        isOpen={true}
        onClose={() => {
          setShowPayment(false);
          handleClose();
        }}
        booking={createdBooking}
        onPaymentComplete={handlePaymentComplete}
      />
    );
  }

  // If showing consultation form, render it instead of booking modal
  if (showConsultation && createdBooking) {
    return (
      <ConsultationForm
        isOpen={true}
        onClose={handleConsultationComplete}
        booking={createdBooking}
      />
    );
  }

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div 
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="booking-modal"
      >
        {/* Header */}
        <div className="sticky top-0 bg-kamit-brown p-6 rounded-t-2xl z-10">
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 text-kamit-beige/80 hover:text-kamit-beige transition-colors"
            data-testid="close-booking-modal"
          >
            <X className="h-6 w-6" />
          </button>
          <h2 className="font-heading text-2xl text-kamit-beige">
            {t.title}
          </h2>
          <p className="text-kamit-beige/70 mt-1">
            {t.subtitle}
          </p>
        </div>

        {/* Success State */}
        {success ? (
          <div className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-green-600" />
            </div>
            <h3 className="font-heading text-2xl text-kamit-brown mb-2">
              {t.success}
            </h3>
            <p className="text-kamit-brown/70 mb-4">
              {t.successMsg}
            </p>
            
            {/* Auto-show consultation form for all services */}
            <div className="bg-kamit-gold/10 border border-kamit-gold/30 rounded-xl p-4 mb-4">
              <p className="text-kamit-brown text-sm mb-4 flex items-center justify-center gap-2">
                <ClipboardList className="h-5 w-5 text-kamit-gold" />
                {t.consultationRequired}
              </p>
              <Button 
                onClick={() => setShowConsultation(true)}
                className="w-full bg-kamit-brown hover:bg-kamit-dark text-kamit-beige rounded-full"
                data-testid="fill-consultation-btn"
              >
                <ClipboardList className="mr-2 h-4 w-4" />
                {t.fillConsultation}
              </Button>
            </div>

            {/* Payment Option */}
            <div className="bg-gradient-to-r from-kamit-gold/10 to-kamit-green/10 border border-kamit-gold/30 rounded-xl p-4 mb-4">
              <p className="text-kamit-brown text-sm mb-4 flex items-center justify-center gap-2">
                <CreditCard className="h-5 w-5 text-kamit-gold" />
                {t.orPay}
              </p>
              <Button 
                onClick={() => setShowPayment(true)}
                className="w-full bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark rounded-full"
                data-testid="pay-now-btn"
              >
                <CreditCard className="mr-2 h-4 w-4" />
                {t.payNow}
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Step 1: Service Selection */}
            <div className="space-y-4">
              {/* Category Selection */}
              <div>
                <label className="block text-sm font-medium text-kamit-brown mb-2">
                  {t.selectCategory} *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="w-full p-3 border border-kamit-brown/20 rounded-xl focus:ring-2 focus:ring-kamit-gold focus:border-transparent bg-white"
                  required
                  data-testid="booking-category-select"
                >
                  <option value="">{t.selectCategory}</option>
                  {Object.keys(services).map(cat => (
                    <option key={cat} value={cat}>
                      {t.categories[cat] || cat}
                    </option>
                  ))}
                </select>
              </div>

              {/* Service Selection */}
              {formData.category && (
                <div>
                  <label className="block text-sm font-medium text-kamit-brown mb-2">
                    {t.selectService} *
                  </label>
                  <select
                    value={services[formData.category]?.find(s => s.name === formData.service)?.id || ''}
                    onChange={(e) => handleServiceChange(e.target.value)}
                    className="w-full p-3 border border-kamit-brown/20 rounded-xl focus:ring-2 focus:ring-kamit-gold focus:border-transparent bg-white"
                    required
                    data-testid="booking-service-select"
                  >
                    <option value="">{t.selectService}</option>
                    {(services[formData.category] || []).map(service => (
                      <option key={service.id} value={service.id}>
                        {service.name} - {service.price} {service.duration ? `(${service.duration})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Step 2: Date & Time */}
            {formData.service && (
              <div className="space-y-4 pt-4 border-t border-kamit-brown/10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Date Selection */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-kamit-brown mb-2">
                      <Calendar className="h-4 w-4" />
                      {t.selectDate} *
                    </label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value, time: '' }))}
                      min={getMinDate()}
                      max={getMaxDate()}
                      className="w-full p-3 border border-kamit-brown/20 rounded-xl focus:ring-2 focus:ring-kamit-gold focus:border-transparent"
                      required
                      data-testid="booking-date-input"
                    />
                    {formData.date && isClosedDay(formData.date) && (
                      <p className="text-red-500 text-sm mt-1">{t.closedDay}</p>
                    )}
                  </div>

                  {/* Time Selection */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-kamit-brown mb-2">
                      <Clock className="h-4 w-4" />
                      {t.selectTime} *
                    </label>
                    {loading ? (
                      <div className="flex items-center justify-center p-3 border border-kamit-brown/20 rounded-xl">
                        <Loader2 className="h-5 w-5 animate-spin text-kamit-gold" />
                      </div>
                    ) : formData.date && !isClosedDay(formData.date) ? (
                      availableSlots.length > 0 ? (
                        <select
                          value={formData.time}
                          onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                          className="w-full p-3 border border-kamit-brown/20 rounded-xl focus:ring-2 focus:ring-kamit-gold focus:border-transparent bg-white"
                          required
                          data-testid="booking-time-select"
                        >
                          <option value="">{t.selectTime}</option>
                          {availableSlots.map(slot => (
                            <option key={slot} value={slot}>{slot}</option>
                          ))}
                        </select>
                      ) : (
                        <p className="text-kamit-brown/60 text-sm p-3 border border-kamit-brown/20 rounded-xl">
                          {t.noSlots}
                        </p>
                      )
                    ) : (
                      <select
                        disabled
                        className="w-full p-3 border border-kamit-brown/20 rounded-xl bg-gray-100"
                      >
                        <option>{t.selectTime}</option>
                      </select>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Customer Info */}
            {formData.time && (
              <div className="space-y-4 pt-4 border-t border-kamit-brown/10">
                <h3 className="font-medium text-kamit-brown flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {t.yourInfo}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-kamit-brown mb-2">
                      {t.name} *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full p-3 border border-kamit-brown/20 rounded-xl focus:ring-2 focus:ring-kamit-gold focus:border-transparent"
                      required
                      data-testid="booking-name-input"
                    />
                  </div>
                  
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-kamit-brown mb-2">
                      <Mail className="h-4 w-4" />
                      {t.email} *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full p-3 border border-kamit-brown/20 rounded-xl focus:ring-2 focus:ring-kamit-gold focus:border-transparent"
                      required
                      data-testid="booking-email-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-kamit-brown mb-2">
                    <Phone className="h-4 w-4" />
                    {t.phone} *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full p-3 border border-kamit-brown/20 rounded-xl focus:ring-2 focus:ring-kamit-gold focus:border-transparent"
                    required
                    data-testid="booking-phone-input"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-kamit-brown mb-2">
                    <FileText className="h-4 w-4" />
                    {t.notes}
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="w-full p-3 border border-kamit-brown/20 rounded-xl focus:ring-2 focus:ring-kamit-gold focus:border-transparent resize-none"
                    data-testid="booking-notes-input"
                  />
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 text-red-600 p-4 rounded-xl text-sm">
                {error}
              </div>
            )}

            {/* Submit Button */}
            {formData.time && (
              <div className="pt-4 space-y-4">
                <Button
                  type="submit"
                  disabled={submitting || !formData.name || !formData.email || !formData.phone}
                  className="w-full bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark rounded-full py-6 text-lg disabled:opacity-50"
                  data-testid="booking-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      {t.submitting}
                    </>
                  ) : (
                    <>
                      <Check className="mr-2 h-5 w-5" />
                      {t.submit}
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Zappy Alternative Link */}
            <div className="pt-4 border-t border-kamit-brown/10 text-center">
              <p className="text-kamit-brown/60 text-sm mb-2">{t.orUseZappy}</p>
              <a 
                href={ZAPPY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-kamit-gold hover:text-kamit-gold/80 font-medium transition-colors"
                data-testid="zappy-link"
              >
                <ExternalLink className="h-4 w-4" />
                {t.zappyLink}
              </a>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BookingModal;
